import type { TagsApproval, TagsRequest } from '../types/tags'

export const AWS_FUNDING_APPROVAL_NAMES = [
	'FinanceTeam',
	'CloudEconomicsTeam',
	'CloudTribe'
] as const

const pendingApproval = (name: string): TagsApproval => ({
	name,
	status: null,
	approverID: '',
	lastUpdated: ''
})

/** Pending Finance, Cloud Economics, and Cloud Tribe. Platform Owner is not an approver. */
export const getAwsFundingApprovals = (): TagsApproval[] =>
	AWS_FUNDING_APPROVAL_NAMES.map((name) => pendingApproval(name))

type FundingRecord = {
	tagsID?: string
	details?: Record<string, any>
	tags?: { details?: Record<string, any> }
}

export const projectEnteredOwnFunding = (
	project: FundingRecord | undefined,
	accountTagsID = ''
) => {
	if (!project?.tagsID) {
		return false
	}

	const fundingChoice =
		project.tags?.details?.fundingChoice || project.details?.fundingChoice
	if (fundingChoice === 'own') {
		return true
	}
	if (fundingChoice === 'inherit') {
		return false
	}

	return Boolean(accountTagsID) && project.tagsID !== accountTagsID
}

/**
 * A cluster inherits the project tag when that project entered its own funding.
 * One such project on the account is used. Several are used only when the cluster
 * application names exactly one of them. Otherwise the account tag is the parent.
 */
export const selectClusterFundingParent = (
	accountTagsID: string,
	projects: FundingRecord[] = [],
	application = ''
): { tagsID: string; kind: 'project' | 'account' } | null => {
	if (!accountTagsID) {
		return null
	}

	const own = projects.filter((project) =>
		projectEnteredOwnFunding(project, accountTagsID)
	)
	const applicationName = application.trim().toLowerCase()
	const named = applicationName
		? own.filter((project) => {
				const name = String(
					project.details?.projectName || project.details?.application || ''
				)
					.trim()
					.toLowerCase()
				return name === applicationName
		  })
		: []

	if (named.length === 1 && named[0].tagsID) {
		return { tagsID: named[0].tagsID, kind: 'project' }
	}
	if (own.length === 1 && own[0].tagsID) {
		return { tagsID: own[0].tagsID, kind: 'project' }
	}

	return { tagsID: accountTagsID, kind: 'account' }
}

export const fundingDetailsFromParent = (details: Record<string, any> = {}) => {
	const copy = { ...details }
	delete copy.applications
	delete copy.fundingChoice
	delete copy.parentTagsID
	return copy
}

export const buildAwsFundingTagRequest = (input: {
	requestorID: string
	tagsID: string
	parentTagsID?: string
	fundingChoice: 'inherit' | 'own'
	details: Record<string, any>
}): TagsRequest => ({
	requestorID: input.requestorID,
	action: 'CREATE',
	status: 'PENDING_APPROVAL',
	tagsID: input.tagsID,
	...(input.parentTagsID ? { parentTagsID: input.parentTagsID } : {}),
	approvalStatus: getAwsFundingApprovals(),
	details: {
		...input.details,
		fundingChoice: input.fundingChoice,
		...(input.parentTagsID ? { parentTagsID: input.parentTagsID } : {})
	}
})
